import { BUTTON_STYLES, SUPPORTED_TRIGGERS } from './constants'
import type { ButtonStyle, ModalButton, ModalRule, ModalTrigger, RepeatPolicy } from './types'

type Raw = Record<string, unknown>

const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)

function ids (v: unknown): number[] | undefined {
  return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : undefined
}

// undefined = поле есть, но не парсится (правило отбрасываем); null = границы нет.
function date (v: unknown): Date | null | undefined {
  if (v === null || v === undefined) return null
  if (typeof v !== 'string') return undefined
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? undefined : d
}

function days (v: unknown): { days: number } | undefined {
  if (!isObj(v)) return undefined
  const d = num(v.days)
  return d === null ? undefined : { days: d }
}

function policy (v: unknown): RepeatPolicy | null {
  if (!isObj(v)) return null
  const p: RepeatPolicy = {}
  const overlay = days(v.after_overlay)
  if (overlay) p.afterOverlay = overlay
  const cta = days(v.after_cta)
  if (cta) p.afterCta = cta
  if (Array.isArray(v.after_close)) {
    p.afterClose = v.after_close
      .filter(isObj)
      .map(e => ({ occurrence: num(e.occurrence), days: num(e.days) }))
      .filter((e): e is { occurrence: number; days: number } => e.occurrence !== null && e.days !== null)
      .sort((a, b) => a.occurrence - b.occurrence)
  }
  const maxShows = num(v.max_shows)
  if (maxShows !== null) p.maxShows = maxShows
  return p
}

function button (v: unknown): ModalButton | null {
  if (!isObj(v)) return null
  const id = num(v.id)
  const text = str(v.text)
  const action = str(v.action)
  if (id === null || text === null || action === null) return null
  const style = BUTTON_STYLES.includes(v.style as ButtonStyle) ? (v.style as ButtonStyle) : 'secondary'
  return { id, text, style, action, payload: str(v.payload) }
}

export function toModalRule (raw: unknown): ModalRule | null {
  if (!isObj(raw) || !isObj(raw.content)) return null

  const modalId = num(raw.modal_id)
  if (modalId === null) return null
  if (!SUPPORTED_TRIGGERS.includes(raw.trigger as ModalTrigger)) return null

  const startAt = date(raw.start_at)
  const endAt = date(raw.end_at)
  if (startAt === undefined || endAt === undefined) return null

  const c = raw.content
  if (!Array.isArray(c.buttons)) return null
  const buttons = c.buttons.map(button)
  if (buttons.some(b => b === null)) return null

  return {
    ruleId: num(raw.rule_id),
    modalId,
    abTestId: num(raw.ab_test_id),
    variationId: num(raw.variation_id),
    revision: num(raw.revision),
    trigger: raw.trigger as ModalTrigger,
    context: isObj(raw.context) ? { courseIds: ids(raw.context.course_ids), dayIds: ids(raw.context.day_ids) } : null,
    startAt,
    endAt,
    repeatPolicy: policy(raw.repeat_policy),
    isDraft: raw.is_draft === true,
    content: {
      imageUrl: str(c.image_url),
      heading: str(c.heading),
      text: str(c.text),
      showClose: c.show_close === true,
      buttons: buttons as ModalButton[]
    }
  }
}

export function toModalRules (body: unknown): ModalRule[] {
  const list = Array.isArray(body) ? body : isObj(body) && Array.isArray(body.data) ? body.data : []
  return list.map(toModalRule).filter((r): r is ModalRule => r !== null)
}
